import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent, CartAddEvent } from '@theme/events';

/**
 * Cart shipping progress component that updates when cart changes
 *
 * @typedef {object} Refs
 * @property {HTMLElement} progressFill - The progress bar fill element
 * @property {HTMLElement} message - The message element showing remaining amount
 *
 * @extends {Component<Refs>}
 */
class CartShippingProgress extends Component {
  /** @type {number} */
  #threshold = 0;

  /** @type {number} */
  #currentTotal = 0;

  connectedCallback() {
    super.connectedCallback();
    
    // Get initial values from data attributes
    const threshold = this.dataset.threshold;
    const current = this.dataset.current;
    this.#threshold = threshold ? parseInt(threshold, 10) : 0;
    this.#currentTotal = current ? parseInt(current, 10) : 0;
    
    // Listen for cart updates
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    
    // Update data attribute when it changes (for morphing/re-renders)
    this.#observeDataAttributes();
    
    // Initial update
    this.#updateProgress();
  }

  /**
   * Observes data-current attribute changes (for when cart drawer morphs/re-renders)
   */
  #observeDataAttributes() {
    const observer = new MutationObserver(() => {
      const current = this.dataset.current;
      const newCurrent = current ? parseInt(current, 10) : 0;
      if (newCurrent !== this.#currentTotal) {
        this.#currentTotal = newCurrent;
        this.#updateProgress();
      }
    });

    observer.observe(this, {
      attributes: true,
      attributeFilter: ['data-current'],
    });

    // Store observer for cleanup
    this.#dataObserver = observer;
  }

  /** @type {MutationObserver | null} */
  #dataObserver = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    if (this.#dataObserver) {
      this.#dataObserver.disconnect();
      this.#dataObserver = null;
    }
  }

  /**
   * Handles cart update events
   * @param {CartUpdateEvent|CartAddEvent} event - The cart update event
   */
  #handleCartUpdate = (event) => {
    // Handle both CartUpdateEvent and CartAddEvent
    // Both events have event.detail.resource containing the cart object
    const cart = event.detail?.resource;
    
    // Cart object should have total_price in cents
    if (cart && typeof cart === 'object' && 'total_price' in cart && typeof cart.total_price === 'number') {
      this.#currentTotal = cart.total_price;
      this.#updateProgress();
    }
    // If cart data not available in event, the component will use initial data attribute value
  };

  /**
   * Updates the progress bar and message based on current cart total
   */
  #updateProgress() {
    const { progressFill, message } = this.refs;
    const progressBar = this.querySelector('[role="progressbar"]');
    const qualifiedDiv = this.querySelector('.cart-shipping-progress__qualified');
    const progressDiv = this.querySelector('.cart-shipping-progress__progress');
    
    if (!this.#threshold || this.#threshold <= 0) {
      // Hide if threshold is not set
      this.style.display = 'none';
      return;
    }
    
    // Hide if cart is empty
    if (this.#currentTotal <= 0) {
      this.style.display = 'none';
      return;
    }
    
    this.style.display = '';
    
    const remaining = Math.max(this.#threshold - this.#currentTotal, 0);
    const percentage = Math.min((this.#currentTotal / this.#threshold) * 100, 100);
    const qualified = this.#currentTotal >= this.#threshold;
    
    // Update progress bar
    if (progressFill) {
      progressFill.style.setProperty('--progress-width', `${percentage}%`);
    }
    
    if (progressBar && progressBar instanceof HTMLElement) {
      progressBar.setAttribute('aria-valuenow', String(this.#currentTotal));
      progressBar.setAttribute(
        'aria-label',
        qualified 
          ? this.querySelector('.cart-shipping-progress__qualified .cart-shipping-progress__message')?.textContent || ''
          : `${this.dataset.messagePrefix || 'Add'} ${this.#formatMoney(remaining)} ${this.dataset.messageSuffix || 'more for free shipping'}`
      );
    }
    
    // Show/hide qualified message, but always show progress bar
    if (qualified) {
      if (qualifiedDiv && qualifiedDiv instanceof HTMLElement) qualifiedDiv.style.display = '';
      // Keep progress bar visible when qualified
      if (progressDiv && progressDiv instanceof HTMLElement) progressDiv.style.display = '';
      // Hide the "remaining amount" message when qualified
      if (message && message instanceof HTMLElement) message.style.display = 'none';
    } else {
      if (qualifiedDiv && qualifiedDiv instanceof HTMLElement) qualifiedDiv.style.display = 'none';
      if (progressDiv && progressDiv instanceof HTMLElement) progressDiv.style.display = '';
      
      // Update message if it exists and we have the remaining amount
      if (message && remaining > 0) {
        // Data attributes use kebab-case, accessed as camelCase in dataset
        const prefix = this.dataset.messagePrefix || 'Add';
        const suffix = this.dataset.messageSuffix || 'more for free shipping';
        const remainingFormatted = this.#formatMoney(remaining);
        
        // Update the message, preserving the strong tag structure
        const strongTag = message.querySelector('strong');
        if (strongTag) {
          message.innerHTML = `${prefix} <strong>${remainingFormatted}</strong> ${suffix}`;
        } else {
          message.textContent = `${prefix} ${remainingFormatted} ${suffix}`;
        }
        message.style.display = '';
      } else if (message && remaining <= 0) {
        // Hide message if remaining is 0 or negative
        message.style.display = 'none';
      } else if (message) {
        message.style.display = '';
      }
    }
  }

  /**
   * Formats cents to money string
   * @param {number} cents - Amount in cents
   * @returns {string} Formatted money string
   */
  #formatMoney(cents) {
    // Use Shopify's money format if available
    if (typeof Shopify !== 'undefined' && 'formatMoney' in Shopify && typeof Shopify.formatMoney === 'function') {
      const moneyFormat = (/** @type {any} */ (window).Shop?.moneyFormat) || (/** @type {any} */ (window).money_format) || '{{amount}}';
      return Shopify.formatMoney(cents, moneyFormat);
    }
    
    // Fallback: simple formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
}

if (!customElements.get('cart-shipping-progress')) {
  customElements.define('cart-shipping-progress', CartShippingProgress);
}
